import React, { useCallback } from 'react';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { SelectField as OriginalSelectField } from '../SelectField';

interface IProps<TFieldValues extends FieldValues = FieldValues>
  extends
    UseControllerProps<TFieldValues>,
    Omit<
      React.ComponentProps<typeof OriginalSelectField>,
      'name' | 'value' | 'onChange' | 'error'
    > {
  inputProps?: never;
  isRequired?: boolean;
}

const SelectField = <TFieldValues extends FieldValues>({
  description,
  name,
  rules,
  control,
  defaultValue,
  shouldUnregister,
  isRequired,
  ...selectProps
}: IProps<TFieldValues>) => {
  const intl = useIntl();

  const {
    field: { ref, ...field },
    fieldState: { error },
  } = useController({
    name,
    rules: {
      required: isRequired
        ? {
            value: true,
            message: intl.formatMessage({
              defaultMessage: 'Field is required.',
              id: 'Q4ipsb',
              description: 'Required field validation message',
            }),
          }
        : undefined,
      ...rules,
    },
    control,
    defaultValue,
    shouldUnregister,
  });

  const handleChange = useCallback<
    NonNullable<React.ComponentProps<typeof OriginalSelectField>['onChange']>
  >(
    (_, { value }) => {
      // event doesn't contain value (e.target.value = undefined)
      field.onChange(value);
    },
    [field],
  );

  return (
    <OriginalSelectField
      {...field}
      {...selectProps}
      inputRef={ref}
      onChange={handleChange}
      value={field.value as string} // to make TS happy, maybe we can tell TS only string values accepted here ?
      error={error?.message}
      description={description}
    />
  );
};

export { SelectField };
